/**
 * CenterStatistics Details
 */

// React
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useQuery from '../../utils/hook/useQuery';
// Redux
import { connect } from 'react-redux';
import { compose } from 'redux';
import { getCenterStatisticsWithIdAction } from '../centers/actions';
import { Store } from '../../redux/reducers';

// Selector
import { createStructuredSelector } from 'reselect';
import {
  makeSelectCentersStatistic,
  makeSelectLoading,
  makeSelectError,
} from '../centers/selectors';

// Components
import RenderFilter from '../../utils/listDaynamicFilter';
import List from './components/listDetails';

// UI
import clsx from 'clsx';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Button,
  Grid,
  IconButton,
  InputBase,
  ClickAwayListener,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiAccountOutline,
  mdiMagnify,
  mdiKeyOutline,
  mdiArrowRight,
} from '@mdi/js';

// Helper
import { removeEmptyValueObject } from '../../utils/helpers';

// Type
import { PropsDetails, SelectorPropsDetails } from './interfaces';
import type { Filter } from '../../utils/listDaynamicFilter';
import handleError from '../../utils/handleError';
import handleNotfication from '../../utils/handleNotification';
import centerRepository from 'client-sdk/src/Repository/centers-repository';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },
    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: 0,
      marginTop: '12px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    headerBox: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '28px',
    },
    contentlabelTitle: {
      position: 'relative',
      color: theme.palette.gray.light,
      marginLeft: '40px',
      fontSize: '14px',
      fontWeight: 500,
      '&:before': {
        content: '"-"',
        position: 'absolute',
        left: '-20px',
      },
    },
  })
);

type Inputs = {
  'name.contains': string;
};

const Details: FunctionComponent<PropsDetails> = (props: PropsDetails) => {
  const { register, handleSubmit } = useForm<Inputs>();
  const [openFilterBox, setOpenFilterBox] = useState(false as boolean);
  const [filters, setFilters] = useState([] as Filter[]);
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 10,
  } as any);
  const [loading, setLoading] = React.useState(false);
  const [centersStatistic, setCentersStatistic] = React.useState([]);
  const [error, setError] = React.useState('');

  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش‌دهنده:',
      'customerName.equals': 'نام سفارش‌دهنده:',
      'customerName.contais': 'نام سفارش‌دهنده:',
      centerName: 'انبار:',
      'centerName.equals': 'انبار:',
      'centerName.contains': 'انبار:',
      id: 'شناسه مرسوله:',
      'id.equals': 'شناسه مرسوله:',
      'id.contains': 'شناسه مرسوله:',
      orderId: 'شناسه سفارش:',
      'orderId.equals': 'شناسه سفارش:',
      'orderId.contains': 'شناسه سفارش:',
      deliveryDate: 'تاریخ تحویل',
      'deliveryDate.equals': 'تاریخ تحویل:',
      'deliveryDate.greaterThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.greaterThan': 'تاریخ تحویل:',
      'deliveryDate.lessThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.lessThan': 'تاریخ تحویل:',
      packagedDate: 'تاریخ تحویل به لجستیک:',
      'packagedDate.equals': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThan': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThan': 'تاریخ تحویل به لجستیک:',
      status: 'وضعیت سفارش:',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
      partnerName: 'شرکت:',
      'partnerName.equals': 'شرکت:',
      'partnerName.contains': 'شرکت:',
      weight: 'وزن:',
      'weight.equals': 'وزن:',
      'weight.contains': 'وزن:',
      length: 'ابعاد:',
      'length.equals': 'ابعاد:',
      'length.contains': 'ابعاد:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };
  const handleClick = () => {
    setOpenFilterBox(prev => !prev);
  };

  const handleClickAway = (e: any) => {
    // TODO: Shet
    if (
      e.target.getAttribute('dir') !== 'rtl' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('class') !== 'PrivateSwitchBase-input-73' &&
      e.target.getAttribute('class') !==
      'MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock' &&
      e.target.getAttribute('aria-hidden') !== 'true'
    )
      setOpenFilterBox(false);
  };

  useEffect(() => {
    getCenterStatistics();
    setupFilters();
  }, [filtersValues]);
  const classes = useStyles();

  const onSubmit = (filtervalues: any) => {
    setFiltersValues({
      ...removeEmptyValueObject(filtervalues),
      // page: 0,
      // size: 10,
    });
    setOpenFilterBox(false);
  };

  const centerStatisticsWithId = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const response = await centerRepository.getCenterStatisticWithId({
        payload,
      });
      setCentersStatistic(response.data.content);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const getCenterStatistics = () => {
    const { page, size } = filtersValues;
    const { id } = props.match.params;
    centerStatisticsWithId({
      id,
      pagination: { ...removeEmptyValueObject(filtersValues), page, size },
    });
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'name.contains',
        label: 'نام محصول',
        icon: mdiAccountOutline,
      },

      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه محصول',
        icon: mdiKeyOutline,
      },
    ]);
  };
  const headCells = [
    { type: 'input', id: 'name', label: 'نام  محصول' },
    { type: 'input', id: 'id', label: 'شناسه محصول' },
    { type: 'input', id: 'beforeCount', label: 'روزهای گذشته' },
    { type: 'input', id: 'todayCount', label: 'امروز' },
    { type: 'input', id: 'tomorrowCount', label: 'فردا' },
    { type: 'input', id: 'afterTomorrowCount', label: 'پس فردا' },
    { type: 'input', id: 'stockCount', label: 'موجودی' },
    { type: 'input', id: 'stockCount', label: 'کسری' },
  ];
  const head = { headCells };
  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        <Grid container item xs={12} sm={6} alignItems="center">
          <div className={classes.headerBox}>
            <Typography variant="h6" component="h2">
              {`سفارش‌های مرکز
           ${props.match.params?.nameCenter}
            `}
            </Typography>
            <span className={classes.contentlabelTitle}>
              {props.match.params?.id}
            </span>
          </div>
          <Button
            aria-label="details"
            size="medium"
            onClick={() => window.history.back()}
          >
            <Icon path={mdiArrowRight} size={1} />
            {`بازگشت`}
          </Button>
        </Grid>

        <Grid item xs={12} sm={6}>
          <div className={classes.searchBox}>
            <form onSubmit={handleSubmit(onSubmit)} className={classes.input}>
              <IconButton
                type="submit"
                className={classes.iconButton}
                aria-label="search"
              >
                <Icon
                  path={mdiMagnify}
                  size={1}
                  className={classes.magnifyIcon}
                />
              </IconButton>
              <InputBase
                className={classes.input}
                name="name.contains"
                placeholder="نام محصول "
                inputRef={register({ required: true })}
                inputProps={{ 'aria-label': 'search google maps' }}
              />
            </form>
            <ClickAwayListener onClickAway={handleClickAway}>
              <div>
                <Button
                  type="button"
                  onClick={handleClick}
                  className={classes.iconButton}
                  aria-label="search"
                >
                  فیلتر‌ها
                </Button>
                {openFilterBox ? (
                  <div
                    className={clsx(classes.filterBox, {
                      [classes.filterOpen]: openFilterBox,
                    })}
                  >
                    <RenderFilter
                      filters={filters}
                      onFilter={(filtervalues: any) => {
                        setFiltersValues({
                          ...removeEmptyValueObject(filtervalues),
                          // page: 0,
                          // size: 10,
                        });
                        setOpenFilterBox(false);
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </ClickAwayListener>
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <List
                data={centersStatistic}
                head={head}
                filterVal={filtersValues}
                onFilterValue={(filtervalues: any) => {
                  setFiltersValues({
                    ...removeEmptyValueObject(filtervalues),
                    ...filtersValues,
                    page: 0,
                    size: 10,
                  });
                }}
                removeFilterHandler={removeFilterHandler}
                filterLabelTranslator={filterTranslator}
              />
            </>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

const mapStateToProps = createStructuredSelector<Store, SelectorPropsDetails>({
  centersStatistic: makeSelectCentersStatistic(),

  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    centerStatisticsWithIdDispatch: (payload: {
      id: number;
      pagination: object;
    }) => dispatch(getCenterStatisticsWithIdAction(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(Details);
